import { useQuery } from '@apollo/client';
import {
    Navigate,
    Outlet,
    useLocation,
    useMatch,
    useSearchParams,
} from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';

import { DrugProgramProvider } from '../../contexts/DrugProgramProvider';
import { VerifyPatientAccountExistenceDocument } from '../../graphql/generated/graphql';
import { decodeContext } from '../../util/notification-context';

import { routes } from './routes';

/**
 * Single layout route for every authenticated area of the app.
 *
 * It deliberately does not enforce authentication itself. Each child route keeps its own
 * `AuthorizedRoute` / `AuthorizedOutlet` guard. What this component owns is the notification
 * deep-link flow, which has to run *before* those guards: a guard would call `signIn` on a
 * new patient who should have been sent to `signUp`.
 */
export const AuthedProviders = () => {
    const { signIn, signUp, isAuthenticated, disableAuthFlows } =
        useAuthContext();
    const [searchParams] = useSearchParams();
    const { pathname, search } = useLocation();

    // A pathless layout route has no params of its own, so match the deep-link patterns
    // against the location rather than calling useParams().
    const orderMatch = useMatch(
        `/${routes.order.root}/${routes.order.prescriberOrderId.root}/*`
    );
    const fillMatch = useMatch(
        `/${routes.fill.root}/${routes.fill.drxId.root}/*`
    );

    const context = searchParams.get('context') ?? '';
    const isDeepLink = Boolean(context) && Boolean(orderMatch ?? fillMatch);
    const returnUrl = `${pathname}${search}`;

    useQuery(VerifyPatientAccountExistenceDocument, {
        variables: { context },
        skip: !isDeepLink || isAuthenticated || disableAuthFlows,
        onCompleted: (data) => {
            const accountExists =
                data?.verifyPatientAccountExistence?.accountExists;

            const authState: Record<string, string | undefined> = {
                drxId: orderMatch?.params.prescriberOrderId,
            };

            // We don't want to show the "opt out" button or other text if the script has already been routed
            // @TODO: ideally, this should be done via GraphQL query to check the status of the drx instead
            const notificationData = decodeContext(context);
            if (
                notificationData?.message.context.templateId.includes(
                    'rx-transfer'
                )
            ) {
                delete authState.drxId;
            }

            if (accountExists) {
                signIn(returnUrl, authState);
            } else {
                signUp(returnUrl, authState);
            }
        },
        onError: (error) => {
            // oxlint-disable-next-line no-console
            console.error('Error checking account existence:', error);
            signIn(returnUrl);
        },
    });

    if (isAuthenticated) {
        return (
            <DrugProgramProvider>
                <Outlet />
            </DrugProgramProvider>
        );
    }

    if (isDeepLink) {
        if (disableAuthFlows) {
            return <Navigate to={routes.root} replace />;
        }

        // Waiting on the verify query to choose signIn vs signUp.
        return null;
    }

    // Signed out with no deep link: the guard on each child route decides.
    return <Outlet />;
};
